export type ChildrenIndicatorFn = ($ctx) => boolean;

export interface ExpandableOptions {
  childKey: string;
  hasChildrenIndicator: string | ChildrenIndicatorFn;
}

const defineProperty = (target, source, key) =>
  Object.defineProperty(target, key, {
    writable: true,
    configurable: true,
    enumerable: true,
    value: source[key],
  });

export class ExpandableResource<T> {
  id: string | number;
  expanded: boolean;
  isContainer: boolean;
  children: Array<ExpandableResource<T>>;
  editMode = false;
  level: number;
  childKey: string;
  entity: string;
  isDir: boolean;

  constructor(data: T, { childKey, hasChildrenIndicator }: ExpandableOptions) {
    this.childKey = childKey;
    if (data[childKey] && data[childKey].length) {
      data[childKey] = data[childKey].map(x => new ExpandableResource<T>(x, { childKey, hasChildrenIndicator }));
    }

    Object.keys(data).forEach(defineProperty.bind(null, this, data));

    const containerIndicator =
      typeof hasChildrenIndicator === 'function'
        ? (hasChildrenIndicator as ChildrenIndicatorFn).bind(null, data)
        : () => !!data[hasChildrenIndicator];

    if (containerIndicator()) {
      this.expanded = data['expanded'] || false;
      Object.defineProperty(this, 'children', {
        get: () => this[childKey],
      });
    }

    Object.defineProperty(this, 'isContainer', {
      configurable: false,
      enumerable: true,
      get: containerIndicator as () => any,
    });
  }

  toggle() {
    if (this.isContainer) {
      this.expanded = !this.expanded;
    }
  }

  insertChild(newItem: ExpandableResource<T>) {
    this[this.childKey] = this.isContainer && this.children ? [...this[this.childKey], newItem] : [newItem];
  }

  setChildren(children: Array<ExpandableResource<T>>): void {
    this[this.childKey] = children;
  }
}

/**
 * It sorts collection with given sortingFn
 * @param collection: Array<ExpandableResource<T>>
 * @param sortingFn: (a: ExpandableResource<T>, b: ExpandableResource<T>) => number
 * @returns sorted collection
 */
export const sortCollectionWith = <T = any>(
  collection: Array<ExpandableResource<T>>,
  sortingFn: (a: ExpandableResource<T>, b: ExpandableResource<T>) => number
) => {
  return collection
    .map(element => {
      if (element.isContainer && element.children && element.children.length) {
        element[element.childKey] = sortCollectionWith(element.children, sortingFn);
      }
      return element;
    })
    .sort(sortingFn);
};

/**
 * Returns the number of leafs in a tree structue array
 * @param treeElements Array<ExpandableResource<T>>
 * @returs number
 */
export const leafCounter = <T = any>(treeElements: Array<ExpandableResource<T>>): number =>
  treeElements.reduce((acc, element) => acc + (element.isContainer ? leafCounter(element.children || []) : 1), 0);

/**
 * It flats Tree to Rows via reduce
 * @param elements: Array<ExpandableResource<T>>
 * @param level: number
 * @returns flattened Array<ExpandableResource<T>> rows
 */
export const treeContainerExtractor = <T = any>(
  elements: Array<ExpandableResource<T>>,
  level = 0
): Array<ExpandableResource<T>> => {
  return elements.reduce((acc, element) => {
    element.level = level;
    if (element.isContainer && element.expanded && element.children) {
      return [...acc, element as ExpandableResource<T>, ...treeContainerExtractor(element.children, level + 1)];
    }
    return [...acc, element];
  }, []);
};
